import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import Loader from '~/components/Loader/Loader'
import { storedToken, useSetToken } from '~/utils/session'
import { Logger } from '~/utils/logging'
import { useOnboardingNavigation } from '~/utils/useOnboardingNavigation'
import { useUserAuthenticated } from '~/utils/useUserAuthenticated'
import { hasSignupDraft } from '~/utils/signupDraft'
import api from '~/api'

const logger = Logger.getInstance()

const NEXT_SCREEN_URL = '/bff/utils/onboarding/next/me/'

// OAuth error codes from `getAccessTokenSilently()` that mean the SSO session
// is gone and the user must interact to authenticate again.
const REAUTH_REQUIRED_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
])

const needsReauth = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false
  const code = (err as { error?: unknown }).error
  return typeof code === 'string' && REAUTH_REQUIRED_CODES.has(code)
}

/**
 * Landing component that redirects users to the appropriate signup flow
 * based on their authentication status.
 *
 * - Unauthenticated users → /signup/user-type
 * - Authenticated users: bootstrap the lucian User via POST /user/authenticate/.
 *   When the pre-signup email-verification gate is active and the email is
 *   unverified, that call 403s and `useUserAuthenticated` redirects to
 *   /emailverification. Otherwise, if a signup draft is cached for this email we
 *   resume account creation; else we ask the onboarding engine for the next
 *   screen and redirect there (or /success when onboarding is already complete).
 */
const Landing: React.FC = () => {
  const history = useHistory()
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0()
  const setToken = useSetToken()
  const userAuthenticated = useUserAuthenticated()
  const routeToNextScreen = useOnboardingNavigation()
  const hasRouted = useRef(false)

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (!isAuthenticated) {
      // Clear any stale/expired token from previous session so unauthenticated
      // API calls don't fail with expired-token errors.
      storedToken.remove()
      history.replace('/signup/user-type')
      return
    }

    // Route the authenticated user once — the async flow below navigates away,
    // so guard against re-entry while it's in flight.
    if (hasRouted.current) {
      return
    }
    hasRouted.current = true

    const routeAuthenticatedUser = async () => {
      try {
        await setToken.handler()

        // Bootstrap the lucian User. On 403 email_not_verified the hook
        // redirects to /emailverification and resolves null — bail out here.
        const authedUser = await userAuthenticated()
        if (!authedUser) return

        // A cached signup draft for this email means the user is mid-signup —
        // resume account creation rather than asking the onboarding engine.
        if (user?.email && hasSignupDraft(user.email)) {
          history.replace('/signup/create-account')
          return
        }

        const {
          data: { next },
        } = await api.rest.get<{ next: string | null }>(NEXT_SCREEN_URL)

        // Replace, so a back gesture cannot land the user back on this landing and
        // re-run the routing.
        routeToNextScreen(next, { replace: true })
      } catch (err) {
        if (needsReauth(err)) {
          // SSO session expired during the silent token refresh. Drop the stale
          // bearer and force re-auth; redirect back to the landing.
          storedToken.remove()
          loginWithRedirect({ redirectUri: window.location.origin + '/' })
          return
        }
        logger.error('Error routing authenticated user on landing')
        history.replace('/error')
      }
    }

    routeAuthenticatedUser()
  }, [
    isAuthenticated,
    isLoading,
    history,
    setToken,
    userAuthenticated,
    routeToNextScreen,
    loginWithRedirect,
    user,
  ])

  return <Loader />
}

export default Landing
